import React, { useEffect, useState } from 'react';
import { CheckCircle, FileText, AlertCircle, ClipboardList, Download, FileDown, Users, Home } from 'lucide-react';
import { auth, db } from '../../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../components/ui/ToastProvider';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { H1, H2 } from '../../components/typography';
import { classifyApplication, isCurrentRental, rentalPhaseLabel, summarizeApplications } from '../../utils/applicationStats';

export default function DashboardOverview() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ownerNames, setOwnerNames] = useState({});
  const [associationId, setAssociationId] = useState(null);
  const [currentHousingCooperative, setCurrentHousingCooperative] = useState('');
  const [apartmentCount, setApartmentCount] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchApplicationsForUser(user.uid);
      } else {
        setError('Ingen användare inloggad');
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  async function fetchApplicationsForUser(uid) {
    setLoading(true);
    setError(null);
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) throw new Error('Kunde inte hitta användarinfo');
      
      const userData = userSnap.data();
      const associationId = userData.associationId;
      const housingCooperative = userData.housingCooperative;
      
      if (!associationId) throw new Error('Ingen förening kopplad till användaren');
      
      setAssociationId(associationId);
      setCurrentHousingCooperative(housingCooperative);

      // Hämta housing-data för member name resolution
      const housingQuery = query(
        collection(db, 'housing'),
        where('associationId', '==', associationId)
      );
      const housingSnap = await getDocs(housingQuery);
      const housingData = housingSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Antal lägenheter = antal housing-dokument (INTE medlemsrader —
      // en lägenhet med flera ägare skulle annars räknas dubbelt)
      setApartmentCount(housingSnap.size);

      // Alla föreningens ansökningar — hinkarna räknas ut i applicationStats,
      // inte med en statuslista här (den saknade bland annat TENANT_INFO_SUBMITTED).
      const q = query(
        collection(db, 'applications'),
        where('associationId', '==', associationId)
      );
      const querySnapshot = await getDocs(q);
      // Arkiverade ansökningar (medlem borttagen) räknas inte i översikten.
      const apps = querySnapshot.docs.filter(doc => !doc.data().archivedAt).map(doc => {
        const data = doc.data();

        // Hitta matchande housing för member name
        const matchingHousing = housingData.find(
          h => h.apartmentNumber === data.apartmentNumber && h.address === data.address
        );

        // Resolve owner name from available data sources
        let ownerName = '';
        if (data.owners?.[0]?.firstName && data.owners[0]?.lastName) {
          ownerName = `${data.owners[0].firstName} ${data.owners[0].lastName}`;
        } else if (data.ownerName) {
          // Snapshot sparat på servern när ansökan skapades (utils/ownerSnapshot).
          ownerName = data.ownerName;
        } else if (data.ownerFirstName && data.ownerLastName &&
                   data.ownerFirstName !== 'Okänd' && data.ownerLastName !== 'Ägare') {
          ownerName = `${data.ownerFirstName} ${data.ownerLastName}`;
        } else if (matchingHousing?.members?.length > 0) {
          const m = matchingHousing.members[0];
          ownerName = m.legalEntity || `${m.firstName || ''} ${m.lastName || ''}`.trim();
        }

        return {
          id: doc.id,
          owner: ownerName,
          apt: data.apartmentNumber,
          tenant: `${data.tenantFirstName || ''} ${data.tenantLastName || ''}`.trim(),
          start: data.fromDate,
          end: data.toDate,
          status: data.status,
          ownerUid: data.ownedBy || '',
          ...data
        };
      });
      setApplications(apps);
      // Hämta namn för alla unika ownerUid (om du vill visa namn istället för mail)
      const ownerUids = [...new Set(apps.map(a => a.ownerUid).filter(Boolean))];
      const ownerNameMap = {};
      for (const ownerUid of ownerUids) {
        try {
          const ownerRef = doc(db, 'users', ownerUid);
          const ownerSnap = await getDoc(ownerRef);
          if (ownerSnap.exists()) {
            const owner = ownerSnap.data();
            ownerNameMap[ownerUid] = `${owner.firstName || ''} ${owner.lastName || ''}`.trim();
          } else {
            ownerNameMap[ownerUid] = 'Okänd ägare';
          }
        } catch (err) {
          ownerNameMap[ownerUid] = 'Okänd ägare';
        }
      }
      setOwnerNames(ownerNameMap);
      // // console.log('Hittade ansökningar:', apps);
    } catch (err) {
      setError(err.message || 'Något gick fel vid hämtning av ansökningar');
    } finally {
      setLoading(false);
    }
  }

  // KPI-beräkningar
  // "Godkända/Aktiva" = uthyrningar som upptar lägenheten i dag eller har
  // godkänts men inte börjat — samma definition som fliken i Ansökningar.
  // APPROVED utan hyresgäst är inte en uthyrning, och en passerad period
  // räknas inte längre. Fasen kommer ur datumen, inte ur ett lagrat fält.
  const stats = summarizeApplications(applications);
  const activeRentals = applications.filter(a => isCurrentRental(a));
  const pendingApps = applications.filter(a => a.status === 'PENDING' || a.status === 'ADDITIONAL_INFO');
  const creditCheckCompleteApps = applications.filter(a => a.status === 'CREDIT_CHECK_COMPLETE');
  // Förlängningar väntar på styrelsens beslut men syntes ingenstans på
  // startsidan — bara som en chip långt in i ansökningslistan. Föreningen fick
  // ett mejl och kunde sedan inte se vem det gällde.
  const extensionRequests = applications.filter(a => a.extensionRequest?.status === 'pending');
  // Pågående = ansökningar som väntar på kreditkontroll/hyresgästinfo
  // (godkända men inte slutförda än)
  const ongoingApps = applications.filter(a => ['awaitingTenant', 'creditCheck'].includes(classifyApplication(a)));
  const expiringSoon = applications.filter(a => classifyApplication(a) === 'expiringSoon');

  // KPI navigation functions - navigerar direkt till Applications-sidan
  // Filtreringen sker automatiskt baserat på default-tab "All"
  const handleActiveRentalsClick = () => {
    // Visar godkända och aktiva uthyrningar
    navigate('/dashboard/applications', { state: { initialTab: 'Active' } });
  };

  const handleNewApplicationsClick = () => {
    // Visar ansökningar som behöver granskas
    navigate('/dashboard/applications', { state: { initialTab: 'Pending' } });
  };

  const handleOngoingApplicationsClick = () => {
    // Visar alla ansökningar som väntar på kreditkontroll
    navigate('/dashboard/applications', { state: { initialTab: 'CreditCheckComplete' } });
  };

  const handleExpiringContractsClick = () => {
    // Visar aktiva uthyrningar (kan filtreras manuellt i Applications-vyn)
    navigate('/dashboard/applications', { state: { initialTab: 'Active' } });
  };

  // Action items som kräver BRF-admin åtgärd
  const actionItems = [
    ...(pendingApps.length > 0 ? [{
      count: pendingApps.length,
      label: pendingApps.length === 1 ? 'ny ansökan att granska' : 'nya ansökningar att granska',
      onClick: handleNewApplicationsClick,
      icon: 'pending'
    }] : []),
    ...(creditCheckCompleteApps.length > 0 ? [{
      count: creditCheckCompleteApps.length,
      label: creditCheckCompleteApps.length === 1 ? 'kreditcheck klar - slutligt beslut krävs' : 'kreditchecker klara - slutliga beslut krävs',
      onClick: () => navigate('/dashboard/applications?filter=credit_check_complete'),
      icon: 'credit'
    }] : []),
    ...(extensionRequests.length > 0 ? [{
      count: extensionRequests.length,
      // Med en enda begäran namnger vi medlemmen direkt — det var precis det
      // som saknades: mejlet berättade att någon begärt förlängning, inte vem.
      label: extensionRequests.length === 1
        ? `förlängning begärd${extensionRequests[0].owner ? ` av ${extensionRequests[0].owner}` : ''} — beslut krävs`
        : 'förlängningar begärda - beslut krävs',
      onClick: () => navigate('/dashboard/applications?filter=extension'),
      icon: 'extension'
    }] : [])
  ];

  const kpis = [
    {
      value: activeRentals.length,
      label: 'Godkända/Aktiva',
      color: 'text-gray-700',
      onClick: handleActiveRentalsClick,
      description: stats.upcoming > 0
        ? `${stats.upcoming} har inte börjat än`
        : 'Visa alla godkända och aktiva uthyrningar'
    },
    {
      value: pendingApps.length,
      label: 'Att granska',
      color: 'text-gray-700',
      onClick: handleNewApplicationsClick,
      description: 'Visa ansökningar som behöver granskas'
    },
    {
      value: ongoingApps.length,
      label: 'Pågående process',
      color: 'text-gray-700',
      onClick: handleOngoingApplicationsClick,
      description: 'Visa alla ansökningar i process'
    },
    {
      value: expiringSoon.length,
      label: 'Löper ut snart',
      color: 'text-gray-700',
      onClick: handleExpiringContractsClick,
      description: 'Visa avtal som löper ut inom 30 dagar'
    },
  ];

  const handleViewAllApplications = () => {
    navigate('/dashboard/applications');
  };

  const handleViewApplications = () => {
    navigate('/dashboard/applications');
  };

  const handleViewDocuments = () => {
    navigate('/dashboard/documents');
  };

  const handleExportList = () => {
    const pdfDoc = new jsPDF({ orientation: 'landscape' });
    const brfName = currentHousingCooperative || 'BRF';
    const dateStr = new Date().toLocaleDateString('sv-SE');

    // Klassificera uthyrningar per status
    const now = new Date();
    const soon = new Date();
    soon.setDate(now.getDate() + 30);

    const classify = (r) => {
      const endDate = r.end ? new Date(r.end) : (r.toDate ? new Date(r.toDate) : null);
      if (endDate && endDate < now) return 'avslutad';
      if (endDate && endDate >= now && endDate <= soon) return 'loperUt';
      return 'aktiv';
    };

    const sortByApt = (a, b) => {
      const aptA = parseInt(a.apt || a.apartmentNumber) || 0;
      const aptB = parseInt(b.apt || b.apartmentNumber) || 0;
      if (aptA !== aptB) return aptA - aptB;
      return ((a.address || '') + '').localeCompare((b.address || '') + '', 'sv');
    };

    const aktiva = activeRentals.filter(r => classify(r) === 'aktiv').sort(sortByApt);
    const loperUt = activeRentals.filter(r => classify(r) === 'loperUt').sort(sortByApt);
    const avslutade = activeRentals.filter(r => classify(r) === 'avslutad').sort(sortByApt);

    // Header
    pdfDoc.setFont('helvetica', 'bold');
    pdfDoc.setFontSize(16);
    pdfDoc.text('Uthyrningar - ' + brfName, 14, 18);
    pdfDoc.setFont('helvetica', 'normal');
    pdfDoc.setFontSize(10);
    pdfDoc.text(`Genererad: ${dateStr}`, 14, 26);

    // Sammanfattning
    pdfDoc.autoTable({
      startY: 32,
      head: [['Kategori', 'Antal']],
      body: [
        ['Aktiva', String(aktiva.length)],
        ['Löper ut snart (inom 30 dagar)', String(loperUt.length)],
        ['Avslutade', String(avslutade.length)],
        ['Totalt', String(activeRentals.length)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [98, 184, 233] },
      styles: { font: 'helvetica', fontSize: 10 },
    });

    const formatPeriod = (r) => {
      const start = r.start || r.fromDate || '';
      const end = r.end || r.toDate || '';
      if (start && end) return `${start} – ${end}`;
      return start || end || '';
    };

    // "Via Hyria" = ansökan har gått genom det digitala flödet.
    // Manuellt tillagda uthyrningar (AddRentalModal) sätter manuallyAdded: true.
    const sourceLabel = (r) => (r.manuallyAdded ? 'Manuell' : 'Via Hyria');

    const renderSection = (title, rentals, statusLabel, headColor) => {
      if (rentals.length === 0) return;
      const startY = pdfDoc.lastAutoTable.finalY + 12;
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.setFontSize(13);
      pdfDoc.setTextColor(0);
      pdfDoc.text(title, 14, startY);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.autoTable({
        startY: startY + 4,
        head: [[
          'Adress',
          'Lägenhet',
          'Bostadsrättsinnehavare',
          'Hyresgäst',
          'Period',
          'Status',
          'Källa',
        ]],
        body: rentals.map(r => [
          r.address || '',
          r.apt || r.apartmentNumber || '',
          ownerNames[r.ownerUid] || r.ownerName || r.owner || '',
          r.tenant || '',
          formatPeriod(r),
          statusLabel,
          sourceLabel(r),
        ]),
        theme: 'grid',
        headStyles: { fillColor: headColor },
        styles: { font: 'helvetica', fontSize: 9, cellPadding: 2 },
        columnStyles: {
          0: { cellWidth: 55 },
          4: { cellWidth: 48 },
        },
      });
    };

    // Sektioner i ordning: Aktiva → Löper ut snart → Avslutad
    renderSection('Aktiva', aktiva, 'Aktiv', [34, 197, 94]);
    renderSection('Löper ut snart', loperUt, 'Löper ut snart', [217, 119, 6]);
    renderSection('Avslutade', avslutade, 'Avslutad', [156, 163, 175]);

    const safeBrf = brfName.replace(/[^a-zåäö0-9]/gi, '_').toLowerCase();
    const safeDate = dateStr.replace(/\//g, '-');
    pdfDoc.save(`uthyrningar_${safeBrf}_${safeDate}.pdf`);
    toast({ type: 'success', message: 'PDF skapad och nedladdad!' });
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8">
      <H1>Översikt</H1>
      {currentHousingCooperative && (
        <p className="text-sm text-zinc-500 mt-1 mb-6">
          {currentHousingCooperative}
          {apartmentCount != null && (
            <>
              {' · '}
              <span
                className="cursor-pointer hover:text-[#62B8E9] underline decoration-dotted underline-offset-2"
                onClick={() => navigate('/dashboard/units')}
                title="Visa medlemsförteckningen"
              >
                {apartmentCount} lägenheter
              </span>
            </>
          )}
        </p>
      )}

      {/* Action Items Alert Box - Visas endast om det finns actions */}
      {!loading && actionItems.length > 0 && (
        <div className="mb-6 bg-gradient-to-r from-[#F9FCFF] to-[#E2EAF0] border-l-4 border-[#62B8E9] rounded-lg shadow-md p-5 animate-[fadeIn_0.3s_ease-in]">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <AlertCircle className="w-6 h-6 text-[#62B8E9]" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-bold text-[#222222] mb-2 font-['Poppins']">
                Kräver din åtgärd ({actionItems.reduce((sum, item) => sum + item.count, 0)})
              </h2>
              <ul className="space-y-2">
                {actionItems.map((item, index) => (
                  <li key={index}>
                    <button
                      onClick={item.onClick}
                      className="flex items-center gap-2 text-[#626262] hover:text-[#62B8E9] transition-colors group"
                    >
                      <span className="w-2 h-2 rounded-full bg-[#62B8E9] group-hover:bg-[#222222] transition-colors"></span>
                      <span className="font-medium">
                        {item.count} {item.label}
                      </span>
                      <span className="text-[#62B8E9] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <>
          {/* Skeletons för KPI-kort */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl shadow p-5 flex items-center gap-4 animate-pulse">
                <div className="rounded-full bg-zinc-100 p-3 w-12 h-12" />
                <div>
                  <div className="h-6 w-16 bg-zinc-100 rounded mb-2" />
                  <div className="h-4 w-24 bg-zinc-100 rounded" />
                </div>
              </div>
            ))}
          </div>
          {/* Skeleton för statistik */}
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1">
              <div className="bg-white rounded-2xl shadow p-6 mb-8 animate-pulse">
                <div className="h-4 w-32 bg-zinc-100 rounded mb-4" />
                <div className="h-16 w-full bg-zinc-100 rounded" />
              </div>
              {/* Skeleton för tabell */}
              <div className="bg-white rounded-2xl shadow p-6 animate-pulse">
                <div className="h-4 w-40 bg-zinc-100 rounded mb-4" />
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-zinc-500">
                        <th className="py-2 pr-4 text-left font-medium">Bostadsrättsinnehavare</th>
                        <th className="py-2 pr-4 text-left font-medium">Lägenhetsnummer</th>
                        <th className="py-2 pr-4 text-left font-medium">Hyresgäst</th>
                        <th className="py-2 pr-4 text-left font-medium">Startdatum</th>
                        <th className="py-2 pr-4 text-left font-medium">Slutdatum</th>
                        <th className="py-2 pr-4 text-left font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(4)].map((_, i) => (
                        <tr key={i} className="border-b last:border-b-0">
                          <td className="py-2 pr-4"><div className="h-4 w-24 bg-zinc-100 rounded" /></td>
                          <td className="py-2 pr-4"><div className="h-4 w-12 bg-zinc-100 rounded" /></td>
                          <td className="py-2 pr-4"><div className="h-4 w-20 bg-zinc-100 rounded" /></td>
                          <td className="py-2 pr-4"><div className="h-4 w-16 bg-zinc-100 rounded" /></td>
                          <td className="py-2 pr-4"><div className="h-4 w-16 bg-zinc-100 rounded" /></td>
                          <td className="py-2 pr-4"><div className="h-4 w-16 bg-zinc-100 rounded" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            {/* Skeleton för sidoknappar */}
            <div className="flex flex-col gap-4 min-w-[220px] md:w-64">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-10 bg-zinc-100 rounded-xl" />
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {kpis.map((kpi, i) => (
            <div 
              key={i} 
              onClick={kpi.onClick}
              className="bg-white rounded-2xl shadow p-5 border border-gray-200 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-[#62B8E9] hover:bg-[#f8fbff] group"
              title={kpi.description}
            >
              <div className="text-2xl font-bold text-zinc-800 mb-1 group-hover:text-[#62B8E9] transition-colors">
                {kpi.value}
              </div>
              <div className="text-sm text-zinc-500 font-medium group-hover:text-[#4a90c2] transition-colors">
                {kpi.label}
              </div>
              <div className="text-xs text-zinc-400 mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                Klicka för att se detaljer →
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex-1">
          <div className="bg-white rounded-2xl shadow p-6 mb-8">
            <H2 sx={{ marginBottom: '0.5rem' }}>Statistik</H2>
            {/* Statistik kan byggas ut med riktig data senare */}
            <svg viewBox="0 0 300 60" className="w-full h-16">
              <polyline
                fill="none"
                stroke="#62B8E9"
                strokeWidth="3"
                points={activeRentals.map((v, i) => `${i * 50},${60 - (v ? 8 : 0)}`).join(' ')}
              />
            </svg>
          </div>
          <div className="bg-white rounded-2xl shadow p-6">
            <H2>Aktiva uthyrningar</H2>
            {loading && (
              <div className="py-8 flex justify-center"><span className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-400"></span></div>
            )}
            {error && (
              <div className="text-red-500 text-center py-4">{error}</div>
            )}
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-zinc-500">
                    <th className="py-2 pr-4 text-left font-medium">Bostadsrättsinnehavare</th>
                    <th className="py-2 pr-4 text-left font-medium">Lägenhetsnummer</th>
                    <th className="py-2 pr-4 text-left font-medium">Hyresgäst</th>
                    <th className="py-2 pr-4 text-left font-medium">Startdatum</th>
                    <th className="py-2 pr-4 text-left font-medium">Slutdatum</th>
                    <th className="py-2 pr-4 text-left font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {activeRentals.map((r, i) => (
                    <tr key={i} className="border-b last:border-b-0">
                      <td className="py-2 pr-4 text-zinc-800">{ownerNames[r.ownerUid] || r.ownerName || r.owner || 'Okänd ägare'}</td>
                      <td className="py-2 pr-4 text-zinc-600">{r.apt}</td>
                      <td className="py-2 pr-4 text-zinc-600">{r.tenant}</td>
                      <td className="py-2 pr-4 text-zinc-600">{r.start}</td>
                      <td className="py-2 pr-4 text-zinc-600">{r.end}</td>
                      <td className="py-2 pr-4">
                        {(() => {
                          // Samma fasetikett som ansökningslistan och ägarvyn:
                          // "Godkänd, startar …" före startdatumet, inte "Aktiv".
                          const phase = rentalPhaseLabel(r) || { text: 'Aktiv', tone: 'blue' };
                          const cls = {
                            green: 'bg-green-100 text-green-800',
                            blue: 'bg-blue-100 text-blue-800',
                            amber: 'bg-amber-100 text-amber-800',
                            gray: 'bg-gray-100 text-gray-700',
                          }[phase.tone];
                          return (
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>
                              {phase.text}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-4 min-w-[220px] md:w-64">
          {/* Snabbfunktioner rubrik */}
          <div className="mb-2">
            <h2 className="text-lg font-semibold border-b border-zinc-200 pb-2 font-['Poppins']">
              Snabbfunktioner
            </h2>
          </div>
          
          <button
            onClick={handleViewAllApplications}
            className="flex items-center gap-2 border border-[#62B8E9] text-[#62B8E9] font-semibold px-5 py-2 rounded-xl bg-white hover:bg-[#e6f3fa] transition"
          >
            <ClipboardList className="w-5 h-5" /> Se alla ärenden
          </button>
          <button 
            onClick={handleExportList}
            className="flex items-center gap-2 border border-[#62B8E9] text-[#62B8E9] font-semibold px-5 py-2 rounded-xl bg-white hover:bg-[#e6f3fa] transition"
          >
            <Download className="w-5 h-5" /> PDF: Uthyrningar
          </button>
          <button 
            onClick={handleViewApplications}
            className="flex items-center gap-2 border border-[#62B8E9] text-[#62B8E9] font-semibold px-5 py-2 rounded-xl bg-white hover:bg-[#e6f3fa] transition"
          >
            <FileText className="w-5 h-5" /> Ansökningar
          </button>
          <button 
            onClick={handleViewDocuments}
            className="flex items-center gap-2 border border-[#62B8E9] text-[#62B8E9] font-semibold px-5 py-2 rounded-xl bg-white hover:bg-[#e6f3fa] transition"
          >
            <Home className="w-5 h-5" /> Dokument
          </button>
        </div>
      </div>

    </div>
  );
} 